import type { UserError } from '../../store/app'
import type { ToastData } from './Toast'

/**
 * Convert a UserError to toast data with beginner-friendly messages.
 */
export function userErrorToToast(error: UserError): Omit<ToastData, 'id'> {
  switch (error.type) {
    case 'insufficient-balance':
      return {
        title: 'Not enough money',
        message: `You need $${error.required.toFixed(2)} but only have $${error.available.toFixed(2)}. Try a smaller order or wait until you have more funds.`,
        severity: 'warning',
      }

    case 'insufficient-shares':
      return {
        title: 'Not enough shares',
        message: `You tried to sell ${error.required} shares but only have ${error.available} available. Reduce the order or cancel another sell order first.`,
        severity: 'warning',
      }

    case 'invalid-price':
      return {
        title: 'Price out of range',
        message: `${error.message} Prices represent probability, so they must be between 1 cent and 99 cents.`,
        severity: 'warning',
      }

    case 'invalid-quantity':
      return {
        title: 'Invalid quantity',
        message: `${error.message} Enter a whole number of shares to buy or sell.`,
        severity: 'warning',
      }

    case 'no-liquidity':
      return {
        title: error.side === 'buy' ? 'No sellers available' : 'No buyers available',
        message:
          error.side === 'buy'
            ? "There's no one selling right now. Try placing a limit order at your desired price instead."
            : "There's no one buying right now. Try placing a limit order at your desired price instead.",
        severity: 'info',
        actionText: 'Use limit order',
      }

    case 'invalid-order':
      return {
        title: 'Order rejected',
        message: `Your order couldn't be placed: ${error.reason}. Please check your inputs and try again.`,
        severity: 'error',
      }

    case 'storage-full':
      return {
        title: 'Storage full',
        message:
          "Your browser's storage is full. Your progress may not be saved. Try clearing some browser data.",
        severity: 'warning',
      }

    case 'storage-corrupted':
      return {
        title: 'Data error',
        message:
          'Some saved data was corrupted and has been reset. Your previous progress may be lost.',
        severity: 'error',
      }

    case 'unknown':
    default:
      return {
        title: 'Something went wrong',
        message: error.type === 'unknown' ? error.message : 'An unexpected error occurred. Please try again.',
        severity: 'error',
      }
  }
}
